'use client';

import { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';

interface RealtimeEvent {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  timestamp: string;
  userId?: string;
}

interface RealtimeContextType {
  isConnected: boolean;
  connect: () => void;
  disconnect: () => void;
  lastEvent: RealtimeEvent | null;
  events: RealtimeEvent[];
  connectionError: Error | null;
}

const RealtimeContext = createContext<RealtimeContextType | undefined>(undefined);

const API_BASE = (import.meta as any).env?.VITE_API_BASE || '/v1';

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<RealtimeEvent | null>(null);
  const [events, setEvents] = useState<RealtimeEvent[]>([]);
  const [connectionError, setConnectionError] = useState<Error | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const connect = () => {
    if (eventSourceRef.current) return;

    try {
      const eventSource = new EventSource(`${API_BASE}/v1/events`, { withCredentials: true });
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setIsConnected(true);
        setConnectionError(null);
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as RealtimeEvent;
          setLastEvent(data);
          setEvents(prev => [data, ...prev.slice(0, 99)]);
        } catch (e) {
          console.error('Failed to parse SSE message:', e);
        }
      };

      eventSource.onerror = () => {
        setIsConnected(false);
        setConnectionError(new Error('Connection lost'));
        eventSource.close();
        eventSourceRef.current = null;
        
        // Auto-reconnect after 5 seconds
        setTimeout(() => {
          connect();
        }, 5000);
      };
    } catch (err) {
      setConnectionError(err instanceof Error ? err : new Error('Failed to connect'));
    }
  };

  const disconnect = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setIsConnected(false);
    }
  };

  useEffect(() => {
    connect();
    return () => disconnect();
  }, []);

  return (
    <RealtimeContext.Provider value={{
      isConnected,
      connect,
      disconnect,
      lastEvent,
      events,
      connectionError,
    }}>
      {children}
    </RealtimeContext.Provider>
  );
}

export function useRealtime() {
  const context = useContext(RealtimeContext);
  if (!context) throw new Error('useRealtime must be used within RealtimeProvider');
  return context;
}